import { useMemo, useState } from "react";

import clsx from "clsx";
import moment from "moment";

import { useModelInsightsTimePeriod } from "@features/mmm/hooks/mmm-url-state";
import { type FixedDateInterval } from "@north-beam/nb-common";
import { ModelInsightsTrackedComponent } from "@pages/mmm/amplitude-tracking/components";
import {
  getMMMEventSelectionData,
  MMMTab,
} from "@pages/mmm/amplitude-tracking/mmm-event";
import { useTrackMMMEvent } from "@pages/mmm/amplitude-tracking/use-track-mmm-event";
import { useModelMetadata } from "@pages/mmm/hooks/use-model-metadata";
import { FlatSelect, type FlatSelectOption } from "@shared/flat-select";
import { DateRangeCalendarSelect } from "@shared/selects/date-range-calendar-select";

const timePeriodOptionRender =
  (label: string) =>
  ({ isSelected }: { isSelected: boolean }) =>
    (
      <div
        className={clsx("px-4 flex-center h-full", {
          "text-white font-semibold": isSelected,
        })}
      >
        {label}
      </div>
    );

export const generateDateRange = (dateValue: string) => {
  switch (dateValue) {
    case "1month": {
      return {
        startDate: moment().subtract(30, "days").toISOString(),
        endDate: moment().toISOString(),
      };
    }
    case "2month": {
      return {
        startDate: moment().subtract(60, "days").toISOString(),
        endDate: moment().toISOString(),
      };
    }
    case "3month": {
      return {
        startDate: moment().subtract(90, "days").toISOString(),
        endDate: moment().toISOString(),
      };
    }
    case "6month": {
      return {
        startDate: moment().subtract(180, "days").toISOString(),
        endDate: moment().toISOString(),
      };
    }
    case "1year": {
      return {
        startDate: moment().subtract(365, "days").toISOString(),
        endDate: moment().toISOString(),
      };
    }
    case "yearToDate": {
      return {
        startDate: moment().startOf("year").toISOString(),
        endDate: moment().toISOString(),
      };
    }
    default: {
      return {
        startDate: moment().subtract(90, "days").toISOString(),
        endDate: moment().toISOString(),
      };
    }
  }
};

export const ModelPeriodSelector = () => {
  const trackMMMEvent = useTrackMMMEvent(MMMTab.MODEL_INSIGHTS);
  const [dateRange, setDateRange] = useModelInsightsTimePeriod();
  const [flatSelectValue, setFlatSelectValue] = useState<string>(
    typeof dateRange === "string" ? dateRange : "custom",
  );
  const [modelMetadata] = useModelMetadata();

  const options = useMemo(() => {
    return [
      {
        label: "ALL",
        value: "all",
        onClick: () => setDateRange("all"),
        renderOption: timePeriodOptionRender("ALL"),
      },
      {
        label: "1M",
        value: "1month",
        onClick: () => setDateRange("1month"),
        renderOption: timePeriodOptionRender("1M"),
      },
      {
        label: "3M",
        value: "3month",
        onClick: () => setDateRange("3month"),
        renderOption: timePeriodOptionRender("3M"),
      },
      {
        label: "6M",
        value: "6month",
        onClick: () => setDateRange("6month"),
        renderOption: timePeriodOptionRender("6M"),
      },
      {
        label: "1Y",
        value: "1year",
        onClick: () => setDateRange("1year"),
        renderOption: timePeriodOptionRender("1Y"),
      },
      {
        label: "YTD",
        value: "yearToDate",
        onClick: () => setDateRange("yearToDate"),
        renderOption: timePeriodOptionRender("YTD"),
      },
      {
        label: "",
        value: "custom",
        renderOption: ({ isSelected }) => (
          <DateRangeCalendarSelect
            calendarClassName="-ml-2 mt-0.5"
            value={
              typeof dateRange === "string"
                ? null
                : { startDate: dateRange.startDate, endDate: dateRange.endDate }
            }
            onApply={(v: FixedDateInterval) => {
              setDateRange(v);
            }}
            maxAllowedDate={
              modelMetadata
                ? moment(modelMetadata.training_end_date)
                : undefined
            }
            maxLookbackDays={
              modelMetadata
                ? moment().diff(
                    moment.utc(modelMetadata.training_start_date),
                    "days",
                  ) + 1
                : undefined
            }
            buttonComponent={(ref, refProps) => (
              <div
                ref={ref}
                {...refProps}
                className={clsx(
                  "h-full flex items-center cursor-pointer px-4",
                  {
                    "text-white": isSelected,
                  },
                )}
              >
                Custom
              </div>
            )}
          />
        ),
      },
    ] as FlatSelectOption<string>[];
  }, [dateRange, setDateRange]);

  const selected = useMemo(() => {
    const option = options.find(({ value }) => value === flatSelectValue);
    if (option) {
      return option;
    }
    return options[1];
  }, [flatSelectValue, options]);

  const setSelected = (option: FlatSelectOption<string>) =>
    setFlatSelectValue(option.value);

  const formattedDateRange = useMemo(() => {
    const { startDate, endDate } =
      typeof dateRange === "string" ? generateDateRange(dateRange) : dateRange;
    return `${moment(startDate).format("MMM DD, YYYY")} - ${moment(
      endDate,
    ).format("MMM DD, YYYY")}`;
  }, [dateRange]);

  return (
    <div>
      <div className="mb-1 text-xs">
        <span className="font-medium text-interstellar+2 mr-2 tracking-wide">
          TIME PERIOD
        </span>
        <span className="text-gray+1">{formattedDateRange}</span>
      </div>
      <FlatSelect
        options={options}
        setSelected={(value) => {
          trackMMMEvent({
            component:
              ModelInsightsTrackedComponent.MODEL_FIT_TIME_PERIOD_SELECTOR,
            action: "onChange",
            data: getMMMEventSelectionData(value.label),
          });

          setSelected(value);
        }}
        selected={selected}
      />
    </div>
  );
};
